import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import AccessibleTable from './AccessibleTable';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import AuthService from '../services/AuthService';
import { AccessRole, AccessUser, PersonAccess, GraduationApi, errorMessage } from '../services/GraduationApi';
import { useImpersonation } from './StudentWorkspace';

export default function ManageAccess() {
  const impersonation = useImpersonation();
  const allowed = Boolean(AuthService.getCurrentToken()?.canImpersonate && !impersonation);
  const [roles, setRoles] = useState<AccessRole[]>([]);
  const [users, setUsers] = useState<AccessUser[]>([]);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<'displayName' | 'starId' | 'techId' | 'roleNames'>('displayName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [loaded, setLoaded] = useState(false);
  const [identifier, setIdentifier] = useState('');
  const [nameMatches, setNameMatches] = useState<PersonAccess['person'][] | null>(null);
  const [validation, setValidation] = useState('');
  const [person, setPerson] = useState<PersonAccess | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [editing, setEditing] = useState(false);
  const [roleName, setRoleName] = useState('');
  const [description, setDescription] = useState('');
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const lifetime = useRef<AbortController | null>(null);
  const personHeading = useRef<HTMLHeadingElement>(null);
  const matchesHeading = useRef<HTMLHeadingElement>(null);
  const roleHeading = useRef<HTMLHeadingElement>(null);
  useEffect(() => { if (nameMatches) matchesHeading.current?.focus(); }, [nameMatches]);
  const visibleUsers = useMemo(() => {
    const term = search.trim().toLocaleLowerCase();
    const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });
    return users.filter(user => !term || [user.displayName, user.starId, user.techId, ...user.roleNames]
      .some(value => (value || '').toLocaleLowerCase().includes(term)))
      .sort((a, b) => {
        const value = (user: AccessUser) => sortKey === 'roleNames' ? user.roleNames.join(', ') : user[sortKey] || '';
        const result = collator.compare(value(a), value(b)) || collator.compare(a.starId, b.starId);
        return sortDirection === 'asc' ? result : -result;
      });
  }, [users, search, sortKey, sortDirection]);
  const changeSort = (key: typeof sortKey) => {
    if (sortKey === key) setSortDirection(direction => direction === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDirection('asc'); }
  };
  const loadAccess = async (signal: AbortSignal) => {
    const [roleData, userData] = await Promise.all([GraduationApi.accessRoles(signal), GraduationApi.accessUsers(signal)]);
    if (!signal.aborted) { setRoles(roleData); setUsers(userData); setLoaded(true); }
  };
  useEffect(() => {
    const controller = new AbortController(); lifetime.current = controller;
    if (allowed) {
      setBusy(true);
      loadAccess(controller.signal)
        .catch(e => { if (!controller.signal.aborted && !axios.isCancel(e)) setError(errorMessage(e)); })
        .finally(() => { if (!controller.signal.aborted) setBusy(false); });
    }
    return () => controller.abort();
  }, [allowed]);
  if (!allowed) return <Navigate to="/" replace />;
  const run = async (action: (signal: AbortSignal) => Promise<void>) => {
    if (busy) return;
    const signal = lifetime.current!.signal;
    setBusy(true); setError(''); setSuccess('');
    try { await action(signal); }
    catch (e) { if (!signal.aborted && !axios.isCancel(e)) setError(axios.isAxiosError(e) && e.response?.status === 404 ? 'No institutional account was found for that StarID or Tech ID.' : errorMessage(e)); }
    finally { if (!signal.aborted) setBusy(false); }
  };
  const openPerson = (value: string) => {
    setIdentifier(value);
    setPerson(null);
    setSelected([]);
    setNameMatches(null);
    setValidation('');
    run(async signal => {
      const result = await GraduationApi.accessPerson(value, signal);
      if (signal.aborted) return;
      setPerson(result);
      setSelected(result.roleNames);
      personHeading.current?.focus();
    });
  };
  const findPerson = () => {
    const value = identifier.trim();
    if (/^(?:\d{8}|[a-z]{2}\d{4}[a-z]{2})$/i.test(value)) {
      openPerson(value);
      return;
    }
    if (value.length < 2) {
      setValidation('Enter at least two characters of a name, a StarID, or an eight-digit Tech ID.');
      return;
    }
    setPerson(null);
    setSelected([]);
    setNameMatches(null);
    setValidation('');
    run(async signal => {
      const matches = await GraduationApi.searchAccessPeople(value, signal);
      if (!signal.aborted) setNameMatches(matches);
    });
  };
  const resetRole = () => { setEditing(false); setRoleName(''); setDescription(''); setActive(true); };
  const dirty = person && (selected.length !== person.roleNames.length || selected.some(r => !person.roleNames.includes(r)));
  return <article>
    <h1>Manage Access</h1>
    <p>Look up faculty or staff, review their identity, and assign the roles they need.</p>
    {error && <Alert variant="danger" role="alert">{error}</Alert>}
    {success && <Alert variant="success" role="status">{success}</Alert>}
    {!loaded && <Button className="mb-3" disabled={busy} onClick={() => run(loadAccess)}>{busy ? 'Loading access…' : 'Retry loading access'}</Button>}
    <fieldset disabled={busy || !loaded}>
      <section className="section-panel mb-4" aria-labelledby="access-person-heading">
        <h2 id="access-person-heading" ref={personHeading} tabIndex={-1} className="h4">Assign roles</h2>
        <Form className="mb-4" onSubmit={e => { e.preventDefault(); findPerson(); }}>
          <Form.Group controlId="access-identifier" className="mb-3" style={{ maxWidth: 560 }}>
            <Form.Label>Faculty, staff or student name, StarID, or Tech ID</Form.Label>
            <Form.Control required maxLength={100} autoComplete="off" value={identifier} isInvalid={Boolean(validation)}
              aria-invalid={Boolean(validation)} aria-describedby={validation ? 'access-identifier-help access-identifier-error' : 'access-identifier-help'}
              onChange={e => { setIdentifier(e.target.value); setPerson(null); setSelected([]); setNameMatches(null); setValidation(''); setError(''); setSuccess(''); }} />
            {validation && <Form.Control.Feedback id="access-identifier-error" type="invalid" role="alert">{validation}</Form.Control.Feedback>}
            <Form.Text id="access-identifier-help">Enter at least two characters of a name, a StarID such as ab1234cd, or an eight-digit Tech ID.</Form.Text>
          </Form.Group>
          <Button type="submit">Find person</Button>
        </Form>
        {nameMatches && <div className="mb-4">
          <h3 ref={matchesHeading} tabIndex={-1} className="h5">Matching people</h3>
          {nameMatches.length ? <>
            <p className="text-secondary">Select a person to review and edit their roles. Showing up to 25 matches; enter more of the name to narrow the list.</p>
            <AccessibleTable scrollLabel="Name search matches" striped hover aria-label="Name search matches">
              <thead><tr><th scope="col">Name</th><th scope="col">StarID</th><th scope="col">Tech ID</th><th scope="col" className="text-end">Action</th></tr></thead>
              <tbody>{nameMatches.map(match => <tr key={match.starId}>
                <th scope="row">{match.displayName}</th><td>{match.starId}</td><td>{match.techId}</td>
                <td className="text-end"><Button size="sm" variant="outline-primary" aria-label={`Select ${match.displayName} (${match.starId})`} onClick={() => openPerson(match.starId)}>Select</Button></td>
              </tr>)}</tbody>
            </AccessibleTable>
          </> : <div className="empty-state">No institutional accounts matched that name. Try a different spelling or search by StarID or Tech ID.</div>}
        </div>}
        {person && <div>
          <h3 className="h5">{person.person.displayName}</h3>
          <dl className="row data-summary"><dt className="col-sm-3">StarID</dt><dd className="col-sm-9">{person.person.starId}</dd>
            <dt className="col-sm-3">Tech ID</dt><dd className="col-sm-9">{person.person.techId}</dd></dl>
          <p>Select roles to grant access. Clear a role to remove it, then save.</p>
          {person.graduateFacultyAccess && <p>Graduate Faculty access comes from the Graduate Faculty roster and cannot be changed here.</p>}
          <fieldset><legend className="h6">Assigned roles</legend>
            {roles.filter(r => r.isActive && r.roleName !== 'GraduateFaculty').map(r => <div className="mb-3" key={r.roleId}>
              <Form.Check id={`access-role-${r.roleId}`} label={r.roleName} checked={selected.includes(r.roleName)}
                onChange={e => { setSuccess(''); setSelected(e.target.checked ? [...selected, r.roleName] : selected.filter(n => n !== r.roleName)); }} />
              {r.description && <p className="small text-secondary ms-4 mb-0">{r.description}</p>}
            </div>)}
          </fieldset>
          {!selected.length && <p className="text-secondary">No roles selected. Saving will remove this person's assigned access.</p>}
          <Button disabled={!dirty} onClick={() => run(async signal => {
            await GraduationApi.saveAccessRoles(person.person.starId, selected, signal); if (signal.aborted) return;
            setPerson(null); setSelected([]); setIdentifier('');
            setSuccess(`Roles saved for ${person.person.displayName}.`);
            personHeading.current?.focus();
            try {
              const refreshedUsers = await GraduationApi.accessUsers(signal);
              if (!signal.aborted) setUsers(refreshedUsers);
            } catch {
              if (!signal.aborted) setSuccess(`Roles saved for ${person.person.displayName}, but the people list could not be refreshed. Reload the page to see the latest access.`);
            }
          })}>Save assigned roles</Button>
          <Button className="ms-2" variant="outline-secondary" disabled={!dirty} onClick={() => { setSelected(person.roleNames); setError(''); }}>Discard changes</Button>
        </div>}
      </section>
      <section className="section-panel mb-4" aria-labelledby="access-users-heading">
        <h2 id="access-users-heading" className="h4">People with assigned roles</h2>
        <p className="text-secondary">Graduate Faculty access follows the Graduate Faculty roster and is not listed here.</p>
        <Form.Group controlId="access-users-search" className="mb-3" style={{ maxWidth: 560 }}>
          <Form.Label>Search people and roles</Form.Label>
          <Form.Control type="search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Name, StarID, Tech ID, or role" />
        </Form.Group>
        <p className="small text-secondary" role="status">Showing {visibleUsers.length} of {users.length} people with active roles.</p>
        <AccessibleTable scrollLabel="Users with assigned roles" striped hover><thead><tr>{([
          ['displayName', 'Name'], ['starId', 'StarID'], ['techId', 'Tech ID'], ['roleNames', 'Roles']
        ] as const).map(([key, label]) => <th scope="col" key={key} aria-sort={sortKey === key ? (sortDirection === 'asc' ? 'ascending' : 'descending') : undefined}>
          <Button variant="link" className="admin-sort-button text-start" onClick={() => changeSort(key)}>
            {label} {sortKey === key ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}
          </Button>
        </th>)}<th scope="col" className="text-end">Actions</th></tr></thead>
          <tbody>{visibleUsers.map(user => <tr key={user.starId} className={person?.person.starId === user.starId ? 'table-active' : undefined}>
            <th scope="row">{user.displayName}</th><td>{user.starId}</td><td>{user.techId || '—'}</td>
            <td>{user.roleNames.join(', ')}</td>
            <td className="text-end"><Button size="sm" variant="outline-primary" aria-label={`Edit access for ${user.displayName}`} onClick={() => openPerson(user.starId)}>Edit access</Button></td>
          </tr>)}</tbody>
        </AccessibleTable>
        {visibleUsers.length === 0 && <div className="empty-state mt-3">{users.length ? 'No people match your search.' : 'No people have active roles.'}</div>}
      </section>
      <section className="section-panel mb-4" aria-labelledby="access-catalog-heading">
        <h2 id="access-catalog-heading" className="h4">Role catalog</h2>
        <AccessibleTable scrollLabel="Available access roles" striped hover><thead><tr><th scope="col">Role</th><th scope="col">Description</th><th scope="col">Status</th><th scope="col">Action</th></tr></thead>
          <tbody>{roles.map(r => <tr key={r.roleId}><th scope="row">{r.roleName}</th><td>{r.description || '—'}</td><td><span className="admin-status-label">{r.roleName === 'GraduateFaculty' ? 'Roster managed' : r.isActive ? 'Active' : 'Inactive'}</span></td>
            <td>{r.roleName === 'GraduateFaculty' ? '—' : <Button size="sm" variant="outline-primary" aria-label={`Edit ${r.roleName}`} onClick={() => {
              setEditing(true); setRoleName(r.roleName); setDescription(r.description || ''); setActive(r.isActive); roleHeading.current?.focus();
            }}>Edit</Button>}</td></tr>)}</tbody>
        </AccessibleTable>
        <h3 ref={roleHeading} tabIndex={-1} className="h5">{editing ? 'Edit role' : 'Add role'}</h3>
        <Form onSubmit={e => { e.preventDefault(); run(async signal => {
          if (!editing && roles.some(r => r.roleName.toLowerCase() === roleName.trim().toLowerCase())) throw new Error('That role already exists. Use its Edit button.');
          await GraduationApi.saveAccessRole({ roleName: roleName.trim(), description: description.trim() || null, isActive: active }, signal);
          if (signal.aborted) return;
          setSuccess(`Role ${roleName.trim()} saved.`); resetRole();
          try { const updated = await GraduationApi.accessRoles(signal); if (!signal.aborted) setRoles(updated); }
          catch (e) { if (!signal.aborted) { setLoaded(false); throw new Error('The role was saved, but the catalog could not be refreshed. Retry loading roles.'); } }
        }); }} style={{ maxWidth: 680 }}>
          <Form.Group controlId="access-role-name" className="mb-3"><Form.Label>Role name</Form.Label>
            <Form.Control required maxLength={100} pattern="[^|]*" title="Enter a role name without the pipe character (|)."
              aria-describedby="access-role-name-help" readOnly={editing} value={roleName} onChange={e => setRoleName(e.target.value)} />
            <Form.Text id="access-role-name-help">Required. Use up to 100 characters without the pipe character (|). Role names cannot be changed after creation. New roles grant access only to features configured to use them.</Form.Text>
          </Form.Group>
          <Form.Group controlId="access-role-description" className="mb-3"><Form.Label>Description</Form.Label>
            <Form.Control as="textarea" rows={2} maxLength={500} value={description} onChange={e => setDescription(e.target.value)} /></Form.Group>
          <Form.Check id="access-role-active" label="Active" className="mb-3" disabled={roleName.toLowerCase() === 'accessadministrator'} checked={active} onChange={e => setActive(e.target.checked)} />
          <p className="small text-secondary">Remove a role's active memberships before making it inactive.</p>
          <Button type="submit">{editing ? 'Save role' : 'Add role'}</Button>
          {editing && <Button className="ms-2" variant="outline-secondary" onClick={resetRole}>Cancel editing</Button>}
        </Form>
      </section>
    </fieldset>
  </article>;
}
